import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Activity, Plus, ChevronLeft, ChevronRight, MessageCircle, Volume2 } from '../../components/icons';
import { fetchHabits, createHabit, updateHabit, toggleHabitCompletion, type Habit } from '../../lib/progress';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AICoachChat } from '../../components/ai-coach/AICoachChat';
import { AICoachVoice } from '../../components/ai-coach/AICoachVoice';
import { fetchMyAICoachAccess, fetchCoachConfigForRespondent } from '../../lib/ai-coach';
import { supabase } from '../../lib/supabase';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getWeekDates(offset: number): Date[] {
  const today = new Date();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - today.getDay() + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });
}

export function HabitsView() {
  const { user } = useAuth();
  const [habits, setHabits] = useState<Habit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', frequency: 'daily' as 'daily' | 'weekly', target_days_per_week: 5 });

  // AI Coach
  const [chatEnabled, setChatEnabled] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState('');
  const [respondentName, setRespondentName] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showVoice, setShowVoice] = useState(false);

  const weekDates = getWeekDates(weekOffset);

  const load = async () => {
    if (!user) return;
    const data = await fetchHabits(user.id);
    setHabits(data);
  };

  useEffect(() => {
    if (!user) return;

    const init = async () => {
      await load();

      // Fetch user name
      const { data: profile } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();
      setRespondentName(profile?.full_name || profile?.email || '');

      // Fetch AI coach access
      const { data: respRecord } = await supabase
        .from('respondents')
        .select('id, coach_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (respRecord?.coach_id) {
        const [access, coachConfig] = await Promise.all([
          fetchMyAICoachAccess(respRecord.id),
          fetchCoachConfigForRespondent(respRecord.coach_id),
        ]);
        if (coachConfig?.isEnabled) {
          setChatEnabled(access ? access.chatEnabled : true);
          setVoiceEnabled(access ? access.voiceEnabled : true);
          setElevenlabsAgentId(coachConfig.elevenlabsAgentId || '');
        }
      }

      setIsLoading(false);
    };

    init();
  }, [user]);

  const handleToggle = async (habit: Habit, date: Date) => {
    if (!user) return;
    const dateStr = date.toISOString().split('T')[0];
    const isNowCompleted = await toggleHabitCompletion(habit.id, user.id, dateStr);
    setHabits(prev => prev.map(h => {
      if (h.id !== habit.id) return h;
      const completions = h.completions || [];
      if (isNowCompleted) {
        return { ...h, completions: [...completions, { id: '', habit_id: h.id, user_id: user.id, completed_date: dateStr, created_at: '' }] };
      }
      return { ...h, completions: completions.filter(c => c.completed_date !== dateStr) };
    }));
  };

  const isCompleted = (habit: Habit, date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (habit.completions || []).some(c => c.completed_date === dateStr);
  };

  const weekCompletions = (habit: Habit) =>
    weekDates.filter(d => isCompleted(habit, d)).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    const created = await createHabit(user.id, { ...form, is_active: true });
    if (created) setHabits(prev => [{ ...created, completions: [] }, ...prev]);
    setShowForm(false);
    setForm({ title: '', description: '', frequency: 'daily', target_days_per_week: 5 });
  };

  const handleArchive = async (habit: Habit) => {
    await updateHabit(habit.id, { is_active: false });
    setHabits(prev => prev.filter(h => h.id !== habit.id));
  };

  const today = new Date();
  const isCurrentWeek = weekOffset === 0;

  if (isLoading) return <LoadingSpinner message="Loading habits..." />;

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-coral-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Habits</h1>
            <p className="text-sm text-chocolate-500">{habits.length} active habits</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Habit
        </button>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">New Habit</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Habit *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Morning meditation" required />
            </div>
            <div>
              <label className="freud-label">Description</label>
              <input className="freud-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional details" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Frequency</label>
                <select className="freud-input" value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value as 'daily' | 'weekly' }))}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <label className="freud-label">Target Days / Week</label>
                <input type="number" min={1} max={7} className="freud-input" value={form.target_days_per_week} onChange={e => setForm(p => ({ ...p, target_days_per_week: Number(e.target.value) }))} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">Create Habit</button>
              <button type="button" onClick={() => setShowForm(false)} className="freud-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Week navigator */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeekOffset(o => o - 1)} className="p-2 rounded-lg text-chocolate-500 hover:bg-cream-100 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm font-medium text-chocolate-700">
          {isCurrentWeek ? 'This Week' : `${weekDates[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
        </span>
        <button onClick={() => setWeekOffset(o => o + 1)} disabled={weekOffset === 0} className="p-2 rounded-lg text-chocolate-500 hover:bg-cream-100 transition-colors disabled:opacity-30">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {habits.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <Activity className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">No habits yet. Build your first habit to start tracking.</p>
        </div>
      ) : (
        <div className="freud-card-elevated overflow-hidden">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_repeat(7,2.5rem)] gap-1 px-4 py-2 bg-cream-50 border-b border-cream-200">
            <div className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide">Habit</div>
            {weekDates.map((d, i) => (
              <div key={i} className={`text-center text-xs font-medium ${d.toDateString() === today.toDateString() ? 'text-sage-600 font-bold' : 'text-chocolate-400'}`}>
                <div>{DAY_LABELS[d.getDay()]}</div>
                <div className="text-[10px]">{d.getDate()}</div>
              </div>
            ))}
          </div>

          {habits.map((habit, idx) => (
            <div key={habit.id} className={`grid grid-cols-[1fr_repeat(7,2.5rem)] gap-1 px-4 py-3 items-center ${idx % 2 === 0 ? '' : 'bg-cream-50/50'}`}>
              <div className="min-w-0 pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-chocolate-900 truncate">{habit.title}</p>
                  {habit.created_by_coach && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sage-100 text-sage-700 rounded-full text-[10px] font-semibold flex-shrink-0">
                      Coach Recommended
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-chocolate-400">{weekCompletions(habit)}/{habit.target_days_per_week} this week</p>
                {habit.identity_shift && (
                  <p className="text-[11px] text-chocolate-400 italic truncate">"{habit.identity_shift}"</p>
                )}
              </div>
              {weekDates.map((d, i) => {
                const isFuture = d > today && d.toDateString() !== today.toDateString();
                const done = isCompleted(habit, d);
                return (
                  <button
                    key={i}
                    onClick={() => !isFuture && handleToggle(habit, d)}
                    disabled={isFuture}
                    className={`w-9 h-9 mx-auto rounded-lg flex items-center justify-center transition-all ${
                      done ? 'bg-sage-500 text-white shadow-sm' : isFuture ? 'bg-cream-100 cursor-default' : 'bg-cream-100 hover:bg-cream-200 text-chocolate-300'
                    }`}
                  >
                    {done && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* AI Coach floating buttons */}
      {(chatEnabled || voiceEnabled) && (
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
          {voiceEnabled && (
            <button
              onClick={() => setShowVoice(true)}
              className="w-12 h-12 rounded-full bg-warm-500 text-white shadow-lg hover:bg-warm-600 transition-colors flex items-center justify-center"
              title="Talk to your AI Coach"
            >
              <Volume2 className="w-5 h-5" />
            </button>
          )}
          {chatEnabled && (
            <button
              onClick={() => setShowChat(true)}
              className="w-12 h-12 rounded-full bg-sage-600 text-white shadow-lg hover:bg-sage-700 transition-colors flex items-center justify-center"
              title="Chat with your AI Coach"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {chatEnabled && (
        <AICoachChat
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          pathwayContext="I'm looking at my habits tracker and would like help staying consistent and building better habits."
        />
      )}

      {voiceEnabled && (
        <AICoachVoice
          isOpen={showVoice}
          onClose={() => setShowVoice(false)}
          agentId={elevenlabsAgentId}
          respondentName={respondentName}
          scores={[]}
          harmonicScores={[]}
        />
      )}
    </div>
  );
}
